import { useEffect, useRef, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useStore, useSelector } from 'statux'

import DashboardSettingsMaterialsInputs
    from '../DashboardSettings/DashboardSettingsMaterialsInputs/DashboardSettingsMaterialsInputs'

import Alert from '../../../components/Alert/Alert'
import Form from '../../../components/Form/Form'
import RichText from '../../../components/RichText/RichText'
import TextArea from '../../../components/TextArea/TextArea'

import authProvider from '../../../utils/auth-provider'
import { getCssVar } from '../../../utils/css'
import { graphqlClient, gql } from '../../../utils/gql'
import { scrollTo } from '../../../utils/dom'
import { camelCase } from '../../../utils/strings'

export default function DashboardSettingsAdvancedMembers (props = {
    heading: null,
    richText: null,
    alerts: null,
    steps: null
}) {
    const {
        heading,
        richText,
        steps,
        endDate
    } = props

    const role = authProvider.getDefaultRole()

    if (role === 'admin')
        return window.location.assign(`/admin/#/users/${
            (authProvider.getSession() || {}).sub}`)

    const el = useRef()

    const formStateIndicator = useRef()

    const [modal, setModal] = useStore('modal')

    const [successModal, setSuccessModal] = useState()

    const params = useParams()

    const history = useHistory()

    const cachedProfile = (history.location.state || {}).profile

    const [profile, setProfile] = useState(cachedProfile
            && (cachedProfile.user || {}).id
                === authProvider.getIdentity().id
        ? cachedProfile
        : null)

    const setFormStateIndicator = (state) => formStateIndicator.current
        && (!state
            ? formStateIndicator.current
                .removeAttribute('data-form-state')
            : formStateIndicator.current
                .setAttribute('data-form-state', state))

    let { advancedMembersApplyEndDate } = useSelector('options')

    if (advancedMembersApplyEndDate)
        advancedMembersApplyEndDate = new Date(advancedMembersApplyEndDate)

    const isApplicationEndDate = advancedMembersApplyEndDate
        && advancedMembersApplyEndDate.getTime()
            < (new Date()).getTime()

    const [application, setApplication] = useState({
        complete: false,
        company_materials: (profile || {}).company_materials || null,
        data: (steps || [])
            .filter(obj => obj.slug
                && obj.slug !== 'materials')
            .map(obj => camelCase(obj.slug))
            .reduce((acc, curr, idx) => {
                acc[curr] = null
                return acc
            }, {})
    })

    useEffect(async () => {
        const queryProfile = await graphqlClient
            .request(gql`
                query {
                    profiles (
                        limit: 1,
                        where: {
                            user: { id: { _eq: "${authProvider.getIdentity().id}" } }
                        }
                    ) {
                        id
                        advanced_membership
                        advanced_membership_application
                        company_materials
                    }
                }`)
            .then(obj => (obj.profiles || [])[0])
            .catch((err) => {
                console.warn(err)
                return false
            })

        if (((queryProfile || {}).advanced_membership_application || {}).complete
                    !== application.complete
                || JSON.stringify(application.data)
                    !== JSON.stringify(queryProfile
                        .advanced_membership_application.data)
                || JSON.stringify(application.company_materials)
                    !== JSON.stringify(queryProfile.company_materials))
            setApplication({
                complete: (queryProfile.advanced_membership_application
                    || {}).complete || application.complete,
                company_materials: queryProfile.company_materials,
                data: (queryProfile.advanced_membership_application || {}).data
                    || application.data
            })

        if (JSON.stringify(queryProfile)
                === JSON.stringify(profile))
            return

        setProfile(queryProfile)
    }, [])

    useEffect(() => {
        if (!profile
                || JSON.stringify((history.location.state || {}).profile)
                    === JSON.stringify(profile))
            return

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                profile: profile
            }))
    }, [profile])

    useEffect(async () => {
        if (!application
                || !profile
                || (application.complete
                        === ((profile || {})
                            .advanced_membership_application || {}).complete
                    && JSON.stringify(application.data)
                        === JSON.stringify(((profile || {})
                            .advanced_membership_application || {}).data)
                    && !Object.keys((profile || {}).company_materials)
                        .filter(key => JSON.stringify(profile.company_materials[key])
                            !== JSON.stringify(application.company_materials[key]))
                        .length))
            return

        console.log('Saving...', application)

        setFormStateIndicator('saving')

        const appColName = 'advanced_membership_application'

        const materialsColName = 'company_materials'

        const updateState = JSON.parse(JSON.stringify(application))

        const materials = JSON.parse(JSON.stringify(
            updateState.company_materials))

        delete updateState.company_materials

        const mutation = await graphqlClient
            .request(gql`
                mutation (
                    $${appColName}: jsonb,
                    $${materialsColName}: jsonb
                ) {
                    update_profiles (
                        where: { id: { _eq: "${profile.id}" } }
                        _set: {
                            ${appColName}: $${appColName},
                            ${materialsColName}: $${materialsColName}
                        }
                    ) {
                        returning {
                            id
                            advanced_membership
                            ${appColName}
                            ${materialsColName}
                        }
                    }
                }
            `, {
                [appColName]: updateState,
                [materialsColName]: materials
            }, {
                headers: {
                    'x-hasura-role': 'me'
                }
            })
            .then(obj => ((obj.update_profiles || {})
                .returning || [])[0])
            .catch((err) => {
                console.warn(err)
                return { errors: [] }
            })

        if (!mutation.id) {
            setFormStateIndicator('error')
            setTimeout(() => setFormStateIndicator(), 4000)
            return
        }

        setProfile(mutation)

        if (application.complete) {
            setFormStateIndicator()

            scrollTo([0, parseFloat(getCssVar('--sticky-nav-offset')) || 0], {
                cancelOnUserAction: false
            }).then(() => setSuccessModal({
                className: 'modal-advanced-members',
                icon: 'icon-like',
                heading: 'Thanks for your application!',
                richText: '<p class="margin-3-b">We’ve received your application for advanced membership. We’re hard at work reviewing applicants, and will soon send a follow-up email notifying you of your acceptance status. </p><p><strong>Need help or have questions?<br><a href="mailto:catapult@gse.upenn.edu">catapult@gse.upenn.edu</a></strong></p><p><a href="/dashboard">Return to dashboard</a></p>'
            }))

            return
        }

        setFormStateIndicator('saved')
        setTimeout(() => setFormStateIndicator(), 3000)
    }, [application])

    const onMaterialsChange = (value) => setApplication({
        complete: application.complete,
        company_materials: JSON.parse(value),
        data: application.data
    })

    // Update interal application state to trigger auto-save
    const onUpdate = (e) => {
        if (e.target.tagName !== 'TEXTAREA')
            return

        const data = JSON.parse(JSON.stringify(application.data))

        if (data[camelCase(e.target.name)] === e.target.value)
            return

        data[camelCase(e.target.name)] = e.target.value || null

        setApplication({
            complete: application.complete,
            company_materials: application.company_materials,
            data: data
        })
    }

    const onSubmit = (e) => {
        if (Object.keys(application.data)
            .filter(key => !application.data[key])
            .length)
            return

        setApplication({
            complete: true,
            company_materials: application.company_materials,
            data: application.data
        })
    }

    // Active, prev and next step
    const activeStep = (steps || [])
        .filter(obj => obj.slug === params.step)[0]

    const prevStep = activeStep
        && steps[steps.indexOf(activeStep) - 1]

    const nextStep = activeStep
        ? steps[steps.indexOf(activeStep) + 1]
        : (steps || [])[0]

    console.groupCollapsed('Advanced Membership Application Steps')
    console.log('Active', activeStep)
    console.log('Previous', prevStep)
    console.log('Next', nextStep)
    console.groupEnd()

    const basePathname = (params.step
        ? history.location.pathname
            .replace(new RegExp(`\\/${params.step}\\/*$`), '')
        : history.location.pathname
    ).replace(/\/$/, '')

    useEffect(() => {
        let redirect

        if (role === 'advisor')
            redirect = '/dashboard/settings'

        if (params.step
                && ((activeStep || {}).slug !== params.step
                    || isApplicationEndDate))
            redirect = basePathname

        if (redirect)
            history.replace(redirect)
    }, [params.step])

    useEffect(() => {
        if (successModal
                && application.complete)
            return setModal(successModal)

        if (!activeStep
                || !nextStep)
            return

        const textarea = el.current
            && el.current.querySelector('.form-field-active textarea')

        const activeEl = document.activeElement

        if (!textarea
                || activeEl.classList.contains('focus-visible'))
            return

        textarea.focus()
    }, [history.location.pathname])

    useEffect(() => {
        if (!successModal
                || !application.complete)
            return

        history.push(basePathname)
    }, [successModal])

    useEffect(() => {
        if (!modal || !successModal)
            return

        setSuccessModal(null)
    }, [modal])

    if (profile === null)
        return null

    // Application status
    const isNewApplicant = !profile.advanced_membership_application

    const isApplicationComplete
        = (profile.advanced_membership_application || {}).complete

    const alert = (props.alerts || [])
        .filter(obj => (obj.type === 'complete'
                && isApplicationComplete)
            || (obj.type === 'incomplete'
                && !isApplicationComplete))[0]

    const isStepValid = (slug) => Boolean(slug !== 'materials'
            && application.data[camelCase(slug)])
        || Boolean(slug === 'materials'
            && (profile || {}).company_materials)

    return (
        <section
            className={`dashboard-settings-advanced-members pad-3-t pad-5-b-lg-up${
                activeStep
                    ? ' dashboard-settings-advanced-members-step'
                    : ''}${
                isApplicationComplete
                    ? ' dashboard-settings-advanced-members-complete'
                    : ''}`}
            ref={el}
        >
            <div className="container">
                <div className="row flex-grow flex-justify-content-center">
                    <div className="col-12 col-10-lg-up display-flex flex-direction-column">
                        <div className="flex-none">
                            <Link
                                to={activeStep
                                    ? basePathname
                                    : '/dashboard/settings'}
                                className="body-4 display-inline-flex"
                                style={{
                                    color: getCssVar('--slate'),
                                    marginBottom: 25,
                                    lineHeight: 1.1
                                }}
                                onClick={(e) => {
                                    e.preventDefault()

                                    history.push(activeStep
                                        ? basePathname
                                        : '/dashboard/settings')

                                    console.log(e)
                                }}
                            >
                                <svg
                                    className="icon-chevron-left flex-none"
                                    width="18"
                                    height="18"
                                    style={{ margin: '-2px 2px 0 -5px' }}
                                >
                                    <use xlinkHref="#icon-chevron-left" />
                                </svg>
                                <span>
                                    <span className="display-none-sm-down">Go </span>
                                    <span className="text-transform-capitalize">back </span>
                                    to {!activeStep
                                            || isApplicationEndDate
                                        ? 'Settings'
                                        : 'Application summary'}
                                </span>
                            </Link>
                        </div>
                        <div className="card card-offscreen-x-lg-down pad-5-t-lg-up pad-5-b">
                            <h1
                                className={`heading-2${
                                    !activeStep
                                        ? ' margin-1-b'
                                        : ''}`}
                            >
                                {isApplicationEndDate
                                    ? (endDate || {}).heading
                                        || 'Advanced Membership'
                                    : heading || 'Apply for Advanced Membership'}
                            </h1>
                            {((!activeStep
                                    && !isApplicationEndDate
                                    && richText)
                                || (isApplicationEndDate
                                    && (endDate || {}).richText))
                                && (
                                    <RichText
                                        className="body-3"
                                        html={isApplicationEndDate
                                            ? endDate.richText
                                            : richText}
                                    />)}
                            {!activeStep
                                && !isApplicationEndDate
                                && (
                                    <div className="row margin-3-t">
                                        {nextStep
                                            && (
                                                <div className="col-auto">
                                                    <Link
                                                        className="btn-secondary btn-lg"
                                                        to={`${basePathname}/${
                                                            nextStep.slug}`}
                                                    >
                                                        {isNewApplicant
                                                            ? 'Begin'
                                                            : (isApplicationComplete
                                                                ? 'View'
                                                                : 'Continue')} Application
                                                    </Link>
                                                </div>)}
                                        {(alert || {}).richText
                                            && (
                                                <div className="col col-12-md-down margin-3-t-md-down">
                                                    <Alert
                                                        className={`alert-dashboard-settings-advanced-membership${
                                                            alert.type
                                                                ? `-${alert.type}`
                                                                : ''}`}
                                                        richText={alert.richText}
                                                        type="info"
                                                        role="status"
                                                        dismissible={false}
                                                        fluid
                                                        icon
                                                    />
                                                </div>)}
                                    </div>)}
                            {activeStep
                                && isApplicationComplete
                                && !isApplicationEndDate
                                && (
                                    <Alert
                                        className={`alert-dashboard-settings-advanced-membership${
                                            alert.type
                                                ? `-${alert.type}`
                                                : ''} margin-3-t body-5`}
                                        richText={alert.richText}
                                        type="info"
                                        role="status"
                                        dismissible={false}
                                        fluid
                                        icon
                                    />)}
                            {!isApplicationEndDate
                                && (
                                    <div
                                        className="margin-3-t margin-4-t-md-up margin-5-b"
                                    >
                                        {activeStep
                                            && (
                                                <a
                                                    href="#advanced-membership-application"
                                                    className="skip-to body-3 margin-3-b"
                                                >
                                                    Skip Advanced Membership application navigation
                                                </a>)}
                                        {(steps || []).length
                                            && (
                                                <nav
                                                    className="dashboard-settings-advanced-members-nav"
                                                    aria-label="Advanced Membership Application Sections"
                                                >
                                                    <ol
                                                        className={`row${activeStep
                                                            ? '-condensed flex-nowrap flex-justify-content-space-between-md-down'
                                                            : ''}`}
                                                    >
                                                        {steps
                                                            .map((obj, idx) => (
                                                                <li
                                                                    key={`dashboard-settings-advanced-members-nav-step-${idx}`}
                                                                    className={`dashboard-settings-advanced-members-nav-step ${
                                                                        !activeStep
                                                                            ? 'col-12 margin-b'
                                                                            : 'col text-align-center'}`}
                                                                >
                                                                    <Link
                                                                        to={`${basePathname}/${
                                                                            obj.slug}`}
                                                                        aria-current={`${basePathname}/${
                                                                            obj.slug}` === history.location.pathname}
                                                                    >
                                                                        <div
                                                                            className={`row-condensed${
                                                                                activeStep
                                                                                    ? ' margin-0'
                                                                                    : ''}`}
                                                                        >
                                                                            <div
                                                                                className={`dashboard-settings-advanced-members-nav-step-index col-${
                                                                                    activeStep
                                                                                        ? '12 pad-0'
                                                                                        : 'auto'}`}
                                                                                style={{
                                                                                    '--step-index-line-horz-width-sm':
                                                                                        `${100 / ((steps || [null]).length - 1)}%`,
                                                                                    transitionDelay: isApplicationComplete
                                                                                            && idx
                                                                                            && idx !== steps.length - 1
                                                                                        ? `${idx * 100}ms`
                                                                                        : null
                                                                                }}
                                                                            >
                                                                                <span
                                                                                    className="ratio-square round flex-align-items-center flex-justify-content-center"
                                                                                    aria-label={`${idx + 1}.`}
                                                                                >
                                                                                    {!isStepValid(obj.slug)
                                                                                        ? <span className="display">{idx + 1}</span>
                                                                                        : (
                                                                                            <svg
                                                                                                className={`icon-check flex-none${
                                                                                                    activeStep
                                                                                                        ? ' display-none-md-down'
                                                                                                        : ''}`}
                                                                                                width="18"
                                                                                                height="18"
                                                                                            >
                                                                                                <use xlinkHref="#icon-check" />
                                                                                            </svg>)}
                                                                                </span>
                                                                            </div>
                                                                            <div
                                                                                className={`col${
                                                                                    activeStep
                                                                                        ? ' pad-0'
                                                                                        : ''}`}
                                                                            >
                                                                                <div
                                                                                    className={!activeStep
                                                                                        ? 'display-flex flex-nowrap pad-b'
                                                                                        : null}
                                                                                >
                                                                                    <div className="flex-grow">
                                                                                        <p
                                                                                            className={`dashboard-settings-advanced-members${
                                                                                                activeStep
                                                                                                    ? '-nav'
                                                                                                    : ''}-step-title`}
                                                                                            aria-label={!activeStep
                                                                                                ? `${obj.title}.`
                                                                                                : ''}
                                                                                        >
                                                                                            <span>{obj.title}</span>
                                                                                        </p>
                                                                                        {!activeStep
                                                                                            && obj.text
                                                                                            && <span>{obj.text}</span>}
                                                                                    </div>
                                                                                    {!activeStep
                                                                                        && (
                                                                                            <div className="flex-none display-inline-flex flex-align-items-center pad-l">
                                                                                                <svg
                                                                                                    className="icon-chevron-right"
                                                                                                    width="24"
                                                                                                    height="24"
                                                                                                >
                                                                                                    <use xlinkHref="#icon-chevron-right" />
                                                                                                </svg>
                                                                                            </div>)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Link>
                                                                </li>))}
                                                    </ol>
                                                </nav>)}
                                    </div>)}
                            {activeStep
                                && !isApplicationEndDate
                                && (
                                    <div className="row flex-justify-content-center margin-3-t margin-5-t-md-up margin-5-b">
                                        <div className="col-12 col-10-lg-up">
                                            <h3
                                                id="dashboard-settings-advanced-members-step-title"
                                                className="dashboard-settings-advanced-members-step-title display-flex flex-nowrap"
                                            >
                                                <span className="dashboard-settings-advanced-members-step-index flex-none display-flex flex-align-items-center display-none-md-up margin-1-r">
                                                    <span
                                                        className="ratio-square round flex-align-items-center flex-justify-content-center"
                                                        aria-label={`${steps.indexOf(activeStep) + 1}.`}
                                                    >
                                                        <span className="display">{steps.indexOf(activeStep) + 1}</span>
                                                    </span>
                                                </span>
                                                <span className="flex-grow">
                                                    <span>{activeStep.title}</span>
                                                    {activeStep.slug === 'materials'
                                                        && <span style={{ color: getCssVar('--slate') }}>&nbsp;(Optional)</span>}
                                                </span>
                                            </h3>
                                            {activeStep.richText
                                                && (
                                                    <RichText
                                                        className="body-4 margin-b"
                                                        html={activeStep.richText}
                                                        style={{ lineHeight: 1.5 }}
                                                    />)}
                                            <Form
                                                id="advanced-membership-application"
                                                className={activeStep.slug === 'materials'
                                                    ? 'margin-3-t'
                                                    : 'margin-1-'}
                                                aria-describedby="dashboard-settings-advanced-members-form-state-indicator"
                                                onSubmit={onSubmit}
                                            >
                                                {(steps || [])
                                                    .map((obj, idx) => (
                                                        <div
                                                            key={`form-field-${idx}`}
                                                            className={`${
                                                                obj.slug !== 'materials'
                                                                    ? 'form-field '
                                                                    : ''}${
                                                                obj.slug === activeStep.slug
                                                                    ? 'form-field-active'
                                                                    : 'display-none'}`}
                                                        >
                                                            {obj.slug === 'materials'
                                                                && (
                                                                    <DashboardSettingsMaterialsInputs
                                                                        disabled={isApplicationComplete}
                                                                        defaultValues={profile.company_materials}
                                                                        onChange={onMaterialsChange}
                                                                    />)}
                                                            {obj.slug !== 'materials'
                                                                && (
                                                                    <>
                                                                        <TextArea
                                                                            id={`dashboard-settings-advanced-members-step-${
                                                                                obj.slug}`}
                                                                            aria-labelledby="dashboard-settings-advanced-members-step-title"
                                                                            className="margin-1-t"
                                                                            name={camelCase(obj.slug)}
                                                                            defaultValue={application
                                                                                .data[camelCase(obj.slug)]}
                                                                            autoCapitalize="sentences"
                                                                            autoComplete="off"
                                                                            spellCheck="true"
                                                                            minLength="1"
                                                                            maxLength={1000}
                                                                            placeholder=" "
                                                                            onChange={onUpdate}
                                                                            onBlur={onUpdate}
                                                                            onFocus={onUpdate}
                                                                            readOnly={isApplicationComplete}
                                                                            required
                                                                        />
                                                                        <p
                                                                            id={`dashboard-settings-advanced-members-step-${
                                                                                obj.slug}-invalid`}
                                                                            role="alert"
                                                                        >
                                                                            {activeStep.title} is required
                                                                        </p>
                                                                    </>)}
                                                        </div>))}
                                            </Form>
                                            <div
                                                className="row flex-nowrap flex-align-items-center margin-t margin-4-t-sm-up"
                                            >
                                                <div
                                                    id="dashboard-settings-advanced-members-form-state-indicator"
                                                    className="dashboard-settings-advanced-members-form-state-indicator col body-4"
                                                    ref={formStateIndicator}
                                                >
                                                    <p>
                                                        <svg
                                                            className="icon-info"
                                                            width="16"
                                                            height="16"
                                                        >
                                                            <use xlinkHref="#icon-info" />
                                                        </svg>
                                                        <span>Progress is automatically saved</span>
                                                    </p>
                                                    <p
                                                        data-form-state="saving"
                                                        role="status"
                                                    >
                                                        <svg
                                                            className="icon-sync"
                                                            width="16"
                                                            height="16"
                                                        >
                                                            <use xlinkHref="#icon-sync" />
                                                        </svg>
                                                        <span>Saving...</span>
                                                    </p>
                                                    <p
                                                        data-form-state="saved"
                                                        role="status"
                                                    >
                                                        <svg
                                                            className="icon-check-circle"
                                                            width="16"
                                                            height="16"
                                                        >
                                                            <use xlinkHref="#icon-check-circle" />
                                                        </svg>
                                                        <span>Saved!</span>
                                                    </p>
                                                    <p
                                                        data-form-state="error"
                                                        role="alert"
                                                    >
                                                        <svg
                                                            className="icon-error"
                                                            width="16"
                                                            height="16"
                                                        >
                                                            <use xlinkHref="#icon-error" />
                                                        </svg>
                                                        <span>An error occurred. Please try again.</span>
                                                    </p>
                                                </div>
                                                <div className="col-auto">
                                                    <div className="dashboard-settings-advanced-members-controls">
                                                        <div
                                                            className={`row flex-align-items-center flex-justify-content-${
                                                                prevStep
                                                                    ? 'space-between'
                                                                    : 'end'}-md-down`}
                                                        >
                                                            {(prevStep || {}).slug
                                                                && (
                                                                    <div className="col-auto pad-1-r-sm-down">
                                                                        <Link
                                                                            className="font-weight-medium"
                                                                            to={`${basePathname}/${
                                                                                prevStep.slug}`}
                                                                            aria-label="Previous section"
                                                                            style={{
                                                                                color: getCssVar('--slate')
                                                                            }}
                                                                        >
                                                                            Previous
                                                                        </Link>
                                                                    </div>)}
                                                            {(nextStep || {}).slug
                                                                && (
                                                                    <div
                                                                        className="col-auto pad-1-l-sm-down"
                                                                    >
                                                                        <Link
                                                                            className="btn-secondary btn-lg"
                                                                            to={`${basePathname}/${
                                                                                nextStep.slug}`}
                                                                            aria-label="Next section"
                                                                        >
                                                                            Next
                                                                        </Link>
                                                                    </div>)}
                                                            {!application.complete
                                                                && !nextStep
                                                                && (
                                                                    <div className="col-auto pad-1-l-sm-down">
                                                                        <button
                                                                            className="btn-secondary btn-lg pad-2-l-sm-down pad-r-sm-down"
                                                                            type="submit"
                                                                            form="advanced-membership-application"
                                                                            disabled={Boolean(
                                                                                Object.keys(application.data)
                                                                                    .filter(key => !application.data[key])
                                                                                    .length)
                                                                                && profile.company_materials}
                                                                        >
                                                                            Submit application
                                                                        </button>
                                                                    </div>)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>)}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
